import type { Ref } from 'vue';
import type { RouteLocationNormalizedLoaded, Router } from 'vue-router';

import type { RowRouteAction } from '@/components/datatable/dataTableTypes';

/**
 * Options for {@link useDataTableRowNavigation}.
 */
export type UseDataTableRowNavigationOptions<Row> = {
  /**
   * The current route instance (used to read params like `component` and `id`).
   */
  route: RouteLocationNormalizedLoaded;

  /**
   * Vue Router instance used to perform the navigation.
   */
  router: Router;

  /**
   * Table key / sub-resource segment used in subcomponent routing:
   * `/${component}/${parentId}/${dataKey}/${rowId}`
   */
  dataKey: Ref<string>;

  /**
   * Whether the DataTable is rendered as a nested subcomponent resource.
   * - `true`: navigate under `/${component}/${parentId}/${dataKey}/...`
   * - `false`: navigate under `/${component}/...`
   */
  isSubcomponent: boolean | undefined;

  /**
   * Function to obtain a stable row ID (string/number).
   * Must return null when no ID can be resolved.
   */
  getRowId: (row: Row) => string | number | null;

  /**
   * Current selection (used by `viewSelected` / `editSelected`).
   */
  selectedRowsState: Ref<Row[]>;
};

/**
 * Normalize arbitrary route params (string|number|string[]) into a safe path segment.
 *
 * Notes:
 * - Route params can be arrays in Vue Router when repeated params are present.
 * - For table navigation, we consistently use the first value (if array).
 */
function seg(p: unknown): string {
  // Normalize route params safely into string segments
  if (Array.isArray(p)) {
    return String(p[0] ?? '');
  }
  if (typeof p === 'string' || typeof p === 'number') {
    return String(p);
  }
  return '';
}

/**
 * Navigation helper for DataTable row routing.
 *
 * Responsibilities:
 * - Build canonical detail URLs for a row (view/edit).
 * - Support both "root resource" and "subcomponent resource" routing schemes.
 * - Provide convenience methods that operate on the currently selected row.
 *
 * Routing schemes:
 * - Root:        `/${component}/${rowId}` and `/${component}/${rowId}/edit`
 * - Subresource: `/${component}/${parentId}/${dataKey}/${rowId}` (+ `/edit`)
 *
 * Assumptions:
 * - The route contains `params.component` for the resource name (e.g. "contacts").
 * - In subcomponent mode, the route contains `params.id` for the parent resource id.
 *
 * Non-goals:
 * - Deciding when to navigate (that remains the DataTable component’s policy).
 * - Validating authorization or existence of the target record.
 */
export function useDataTableRowNavigation<Row>(opts: UseDataTableRowNavigationOptions<Row>) {
  const { route, router, dataKey, isSubcomponent, getRowId, selectedRowsState } = opts;

  /**
   * Navigate to a specific row's detail route.
   *
   * @param row - The row object to navigate to.
   * @param action - `"view"` (default) or `"edit"`, influencing the URL suffix.
   *
   * Behavior:
   * - If `getRowId(row)` resolves null, navigation is skipped.
   * - If `isSubcomponent` is true, the URL includes the parent id and table dataKey.
   */
  function navigateToRow(row: Row, action: RowRouteAction = 'view') {
    const rowId = getRowId(row);
    if (rowId == null) return;

    const comp = seg(route.params.component);
    const pid = seg(route.params.id);
    const rid = seg(rowId);

    const suffix = action === 'edit' ? '/edit' : '';
    const target = isSubcomponent
      ? `/${comp}/${pid}/${dataKey.value}/${rid}${suffix}`
      : `/${comp}/${rid}${suffix}`;

    void router.push(target);
  }

  /**
   * Navigate using the current selection.
   *
   * Only navigates when exactly one row is selected (single-record context).
   * If there is no selection or a multi-selection, navigation is skipped.
   */
  function navigateSelectedRow(action: RowRouteAction = 'view') {
    if (selectedRowsState.value.length !== 1) return;
    const row = selectedRowsState.value[0];
    if (!row) return;
    navigateToRow(row, action);
  }

  /**
   * Convenience: navigate to the selected row in view mode.
   */
  function viewSelected() {
    navigateSelectedRow('view');
  }

  /**
   * Convenience: navigate to the selected row in edit mode.
   */
  function editSelected() {
    navigateSelectedRow('edit');
  }

  return {
    navigateToRow,
    navigateSelectedRow,
    viewSelected,
    editSelected,
  };
}
